import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSubscription } from '../../context/SubscriptionContext';
import useSubscriptionApi from '../../hooks/useSubscriptionApi';
import useCountdownRedirect from '../../hooks/useCountdownRedirect';
import '../../styles/SubscriptionSuccess.css';

// Features that are unlocked with subscription
const UNLOCKED_FEATURES = [
    'Kalendarz treningów',
    'Zarządzanie psami',
    'Notatki z treningów',
    'Raporty i analizy',
    'Zarządzanie trenerami',
    'Zaawansowane funkcje'
];

/**
 * SubscriptionSuccess Component
 *
 * Success page shown after completing subscription checkout.
 * Features:
 * - Auto-refreshes subscription status
 * - Shows subscription details
 * - Auto-redirects to dashboard after countdown
 */
const SubscriptionSuccess = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const {
        refreshSubscriptionStatus,
        subscriptionData,
        formatDate
    } = useSubscription();

    const { verifyCheckout } = useSubscriptionApi();

    const [isRefreshing, setIsRefreshing] = useState(true);

    // Countdown redirect hook
    const { countdown } = useCountdownRedirect('/dashboard', 10, !isRefreshing);

    // Verify checkout and refresh subscription on mount
    useEffect(() => {
        const sessionId = new URLSearchParams(location.search).get('session_id');

        const verifyAndRefresh = async () => {
            setIsRefreshing(true);
            try {
                // If we have a session ID from Stripe, verify the checkout
                // This ensures the subscription is updated even if webhook is delayed
                if (sessionId) {
                    console.log('Verifying checkout session:', sessionId);
                    try {
                        const result = await verifyCheckout(sessionId);
                        console.log('Checkout verification result:', result);
                    } catch (verifyErr) {
                        // Log but don't fail - webhook might still process it
                        console.warn('Checkout verification failed (webhook may handle it):', verifyErr);
                    }
                }

                // Always refresh subscription status to get latest data
                await refreshSubscriptionStatus();
            } catch (err) {
                console.error('Error refreshing subscription:', err);
            } finally {
                setIsRefreshing(false);
            }
        };

        verifyAndRefresh();
    }, [location, refreshSubscriptionStatus, verifyCheckout]);

    return (
        <div className="subscription-result-container">
            <div className="result-card success">
                <div className="icon">✔</div>

                <h1>Subskrypcja Aktywowana!</h1>

                <p className="message">
                    Dziękujemy za zakup. Twoje konto zostało zaktualizowane
                    i masz teraz pełny dostęp do wszystkich funkcji.
                </p>

                {isRefreshing ? (
                    <LoadingState />
                ) : (
                    <>
                        <SubscriptionDetails
                            subscriptionData={subscriptionData}
                            formatDate={formatDate}
                        />

                        <UnlockedFeatures features={UNLOCKED_FEATURES} />

                        <p className="redirect-info">
                            Przekierowanie za {countdown} sekund...
                        </p>

                        <div className="actions">
                            <button
                                className="primary-btn"
                                onClick={() => navigate('/dashboard')}
                            >
                                Przejdź do panelu
                            </button>
                            <button
                                className="secondary-btn"
                                onClick={() => navigate('/subscription/plans')}
                            >
                                Zarządzaj subskrypcją
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

/**
 * Loading state while refreshing subscription
 */
const LoadingState = () => (
    <div className="loading">
        <div className="spinner"></div>
        <p>Aktualizowanie danych...</p>
    </div>
);

/**
 * Subscription details section
 */
const SubscriptionDetails = ({ subscriptionData, formatDate }) => {
    if (!subscriptionData) return null;

    return (
        <div className="details">
            <h3>Szczegóły subskrypcji</h3>
            <div className="detail-row">
                <span>Status:</span>
                <strong>
                    {subscriptionData.status === 'ACTIVE' ? 'Aktywna' : subscriptionData.status}
                </strong>
            </div>
            {subscriptionData.endDate && (
                <div className="detail-row">
                    <span>Ważna do:</span>
                    <strong>{formatDate(subscriptionData.endDate)}</strong>
                </div>
            )}
        </div>
    );
};

/**
 * Unlocked features section
 */
const UnlockedFeatures = ({ features }) => (
    <div className="features-activated">
        <h3>Odblokowane funkcje:</h3>
        <div className="features-list">
            {features.map((feature, index) => (
                <span key={index}>{feature}</span>
            ))}
        </div>
    </div>
);

export default SubscriptionSuccess;
